import React, { useEffect } from "react";
import moment from "moment";
import { Row, Col, Form } from "react-bootstrap";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { useMutation } from "react-query";
import { useDispatch } from "react-redux";

import {
	Button,
	ErrorMessage,
	HookFormInput,
	RequiredMark,
	Select,
	TextWithLoader,
} from "components";
import {
	getErrorMessage,
	getFullName,
	hasPermissions,
	permissionList,
} from "helpers";
import { pendService } from "services";
import { uiActions } from "store";
import { PendType, PendStatuses } from "types";
import { required, trimNormalizer, maxLength2000 } from "validators";

import DetailsField from "../components/detailsField";
import PendedToSelect from "../components/pendedToSelect";
import ReasonSelect from "../components/reasonSelect";

import styles from "../pend.module.scss";

type UpdatePendFormProps = {
	loading: boolean;
	pend: PendType;
	reload: any;
	handleBreadcrumb?: Function;
};

type FormData = {
	details: any;
	response: any;
	pendStatus: any;
	status: any;
	reasons: any;
	pendedTo: any;
};

const UpdatePendForm = ({
	loading = false,
	pend,
	reload,
	handleBreadcrumb,
}: UpdatePendFormProps) => {
	const dispatch = useDispatch();

	const { register, handleSubmit, control, formState, setValue } =
		useForm<FormData>({
			mode: "onChange",
			defaultValues: { ...pend },
		});

	const { errors } = formState;

	const { isDirty, isSubmitting } = formState;

	const { mutateAsync: updatePend, isLoading } = useMutation(
		pendService.updatePend,
	);

	const creator = pend?.role?.value === 1;
	const executor = pend?.role?.value === 2;
	const assignee = pend?.role?.value === 3;
	const guest = pend?.role?.value === 4;

	const guestCannotResponse =
		guest && !hasPermissions(permissionList.ViewInProgressApplication);
	const guestCanResponse =
		guest && hasPermissions(permissionList.ViewInProgressApplication);

	const isClosedPend = pend?.status?.value === PendStatuses.closed.value;
	const isOpenedPend = pend?.status?.value === PendStatuses.open.value;
	const isSubmittedPend =
		pend?.status?.value === PendStatuses.response_submitted.value;

	const pendHistory = guestCannotResponse || isClosedPend;
	const userCanEdit = (creator || assignee) && !isClosedPend;

	// On Review
	const pendStatuses = PendStatuses.getOptions({
		predicate(item) {
			if (isOpenedPend) {
				const list = [
					PendStatuses.open.value,
					PendStatuses.closed.value,
				];
				return list.includes(item.value);
			} else {
				const list = [
					PendStatuses.reopen.value,
					PendStatuses.closed.value,
				];
				return list.includes(item.value);
			}
		},
	});

	const placeholderStatus = isSubmittedPend
		? "Response Submitted"
		: "Please Select";

	const createdOn = pend?.createdOn
		? moment.utc(pend?.createdOn).local().format("DD MMMM YYYY h:mm a")
		: "";

	const handleSubmitForm: SubmitHandler<FormData> = ({
		details,
		response,
		status,
		reasons,
		pendedTo,
	}) => {
		const correctedStatus =
			executor || guestCanResponse ? 2 : status?.value; // if executor pend status ResponseSubmitted

		const getReasonIds = reasons => reasons?.map(({ id }) => id);

		updatePend({
			id: pend?.id,
			details: details || pend?.details,
			response,
			status: correctedStatus,
			pendedToId: pendedTo?.id || pend?.pendedTo?.id,
			reasonIds: getReasonIds(reasons || pend?.reasons),
		}).then(
			() => {
				dispatch(
					uiActions.showSuccessNotification({
						title: "Pend has been successfully updated.",
					}),
				);
				handleBreadcrumb?.(status.name);
				reload();
			},
			error => {
				dispatch(
					uiActions.showErrorNotification({
						title: getErrorMessage(error),
					}),
				);
			},
		);
	};

	useEffect(() => {
		if (!isSubmittedPend) setValue("pendStatus", pend?.status); // creator cannot set ResponseSubmitted status
	}, [setValue, pend, isSubmittedPend]);

	return (
		<Form onSubmit={handleSubmit(handleSubmitForm)}>
			<Row>
				<Col xs="6" md="4" lg="3" className="mr-5">
					<Form.Group className="mb-4">
						{userCanEdit ? (
							<PendedToSelect
								errors={errors}
								control={control}
								loading={loading || isLoading}
							/>
						) : (
							<TextWithLoader
								label="Pended to"
								value={getFullName(pend?.pendedTo)}
								loading={loading || isLoading}
								labelCol={12}
								valueCol={12}
							/>
						)}
					</Form.Group>
					<Form.Group className="mb-4">
						<TextWithLoader
							label="Pended on"
							value={createdOn}
							loading={loading || isLoading}
							labelCol={12}
							valueCol={12}
						/>
					</Form.Group>
					<Form.Group className="mb-4">
						{userCanEdit ? (
							<>
								<Form.Label>
									Pend Status
									<RequiredMark />
								</Form.Label>
								<Controller
									render={({ field }) => (
										<Select
											{...field}
											loading={isLoading || loading}
											placeholder={placeholderStatus}
											options={pendStatuses}
											getOptionLabel={option =>
												option.displayName
											}
											getOptionValue={option =>
												option.value
											}
											isSearchable={false}
										/>
									)}
									control={control}
									name="status"
									rules={{
										validate: {
											required,
										},
									}}
								/>
								<ErrorMessage field="status" errors={errors} />
							</>
						) : (
							<TextWithLoader
								label="Pend Status"
								value={pend?.status?.displayName}
								loading={loading || isLoading}
								labelCol={12}
								valueCol={12}
							/>
						)}
					</Form.Group>
					<Form.Group className="mb-4">
						<TextWithLoader
							label="Pend is opened from"
							value={pend?.pendFrom?.displayName}
							loading={loading || isLoading}
							labelCol={12}
							valueCol={12}
						/>
					</Form.Group>
					<Form.Group className="mb-4">
						{userCanEdit ? (
							<ReasonSelect
								errors={errors}
								control={control}
								loading={loading || isLoading}
								initialValue={pend?.reasons}
								setValue={setValue}
							/>
						) : (
							<TextWithLoader
								label="Pend Reason"
								value={pend?.reasons?.map((reason, i) => (
									<div key={i}>{reason?.name}</div>
								))}
								loading={loading || isLoading}
								labelCol={12}
								valueCol={12}
							/>
						)}
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-4">
						{userCanEdit ? (
							<DetailsField
								errors={errors}
								register={register}
								loading={loading || isLoading}
							/>
						) : (
							<Col>
								<TextWithLoader
									label="Pend details"
									value={pend?.details || ""}
									loading={loading || isLoading}
									labelCol={12}
									valueCol={12}
								/>
							</Col>
						)}
					</Form.Group>

					{!pendHistory ? (
						<Form.Group className="mb-4">
							<HookFormInput
								name="response"
								register={register}
								validator={maxLength2000}
								normalizeOnBlur={trimNormalizer}
								colLabel={12}
								colInput={12}
								label="Pend Response"
								placeholder="Response"
								blurLoading={loading || isLoading}
								errors={errors}
								as="textarea"
								inputBlockClassName={styles.textareaWrraper}
							/>
						</Form.Group>
					) : null}
				</Col>
			</Row>
			{!pendHistory ? (
				<Row>
					<Col>
						<Button
							value="Save"
							disabled={!isDirty || isSubmitting}
							type="submit"
						/>
					</Col>
				</Row>
			) : null}
		</Form>
	);
};

export default UpdatePendForm;
